<template>
  <div class="rcb">
    <div v-if="!regrasLocais.length" class="rcb-empty">
      {{ ehQuantidade
        ? 'Nenhuma regra condicional. O anexo segue a quantidade mín/máx fixa acima.'
        : somenteRotular
        ? 'Nenhuma regra de rótulo. O campo mantém o rótulo padrão em qualquer situação.'
        : 'Nenhuma regra. O campo segue visível/obrigatório conforme os interruptores acima.' }}
    </div>

    <div v-for="(regra, idx) in regrasLocais" :key="idx" class="rcb-card">
      <div class="rcb-linha">
        <span class="rcb-prefix">{{ ehQuantidade ? 'Exigir' : 'Tornar' }}</span>

        <template v-if="ehQuantidade">
          <label class="rcb-qtd">mín
            <input type="number" min="0" class="form-control" v-model.number="regra.min" @input="emitir" />
          </label>
          <label class="rcb-qtd">máx
            <input
              type="number"
              min="0"
              class="form-control"
              :value="regra.max == null ? '' : regra.max"
              placeholder="∞"
              @input="(e) => setMax(regra, e.target.value)"
            />
          </label>
        </template>

        <select v-else v-model="regra.efeito" class="form-control rcb-efeito" @change="emitir">
          <option v-for="e in efeitos" :key="e.value" :value="e.value">{{ e.label }}</option>
        </select>

        <!-- Efeito `rotular`: o novo rótulo (payload da regra). Só aparece nesse efeito. -->
        <input
          v-if="!ehQuantidade && regra.efeito === 'rotular'"
          v-model="regra.rotulo"
          class="form-control rcb-rotulo"
          placeholder="novo rótulo (ex.: CPF)"
          @input="emitir"
        />

        <span class="rcb-prefix">quando</span>

        <select v-model="regra.chave" class="form-control" @change="emitir">
          <option v-for="g in gatilhos" :key="g.campo" :value="g.campo">{{ g.label }}</option>
        </select>

        <select v-model="regra.operador" class="form-control" @change="emitir">
          <option v-for="o in operadores" :key="o.value" :value="o.value">{{ o.label }}</option>
        </select>

        <template v-if="regra.operador !== 'preenchido'">
          <!-- Gatilho booleano (ex.: Cliente Simulação, Isento): select Sim/Não em vez de
               texto livre com 'true'/'false' (mais evidente para o usuário). -->
          <select
            v-if="gatilhoBooleano(regra.chave)"
            v-model="regra.valorTexto"
            class="form-control"
            @change="emitir"
          >
            <option value="">—</option>
            <option value="true">Sim</option>
            <option value="false">Não</option>
          </select>
          <input
            v-else
            v-model="regra.valorTexto"
            class="form-control"
            :placeholder="regra.operador === 'em' ? 'ex.: 4711301, 4729699 (vírgula)' : 'valor'"
            @input="emitir"
          />
        </template>

        <CButton
          v-if="!ehQuantidade"
          class="rcb-modelo-btn"
          size="sm"
          :color="jaEhModelo(idx) ? 'success' : 'info'"
          variant="ghost"
          :style="jaEhModelo(idx) ? { cursor: 'default' } : {}"
          v-c-tooltip="{
            content: jaEhModelo(idx)
              ? 'Esta regra já está salva como modelo'
              : 'Salvar esta regra como modelo reutilizável',
            placement: 'top',
          }"
          @click="salvarModelo(idx)"
        >
          <fa-icon :icon="jaEhModelo(idx) ? 'check-circle' : 'save'" />
        </CButton>

        <CButton class="rcb-remover" size="sm" color="danger" variant="ghost" title="Remover regra" @click="remover(idx)">
          <fa-icon icon="times" />
        </CButton>
      </div>

      <div class="rcb-resumo">
        <fa-icon icon="info-circle" class="rcb-resumo-icon" />
        {{ resumo(regra) }}
      </div>
    </div>

    <div class="rcb-acoes">
      <CButton size="sm" color="primary" variant="outline" @click="adicionar">
        <fa-icon icon="plus" /> Adicionar regra
      </CButton>

      <CDropdown
        v-if="!ehQuantidade && modelosDisponiveis.length"
        color="info"
        variant="outline"
        placement="bottom-start"
        toggler-text="Usar modelo"
        class="rcb-usar-modelo"
      >
        <CDropdownItem v-for="m in modelosDisponiveis" :key="m.id" @click="usarModelo(m)">
          {{ m.nome }}
        </CDropdownItem>
      </CDropdown>
    </div>
  </div>
</template>

<script>
import {
  EFEITOS,
  OPERADORES,
  modeloParaRegra,
  modelosAplicaveis,
  regraJaEhModelo,
} from '../helpers/formatarRegra';

export default {
  name: 'RegraCondicionalBuilder',
  props: {
    campo: { type: Object, required: true },
    gatilhos: { type: Array, default: () => [] },
    // Pool global de modelos de regra (para "salvar"/"usar modelo").
    modelos: { type: Array, default: () => [] },
    // 'campo' (padrão): efeitos visível/obrigatório/oculto/opcional.
    // 'quantidade': regra de quantidade (min/máx) por slot de anexo (LP3+ F2).
    modo: { type: String, default: 'campo' },
    // Restringe ao efeito `rotular` (renomear) — usado no PISO estrutural, onde a
    // governança é travada mas o campo ainda pode ser renomeado por condição.
    somenteRotular: { type: Boolean, default: false },
  },
  computed: {
    ehQuantidade() {
      return this.modo === 'quantidade';
    },
    // Efeitos oferecidos no dropdown. Piso estrutural: só `rotular`. Demais: tudo menos 'quantidade'.
    efeitos() {
      if (this.somenteRotular) return EFEITOS.filter((e) => e.value === 'rotular');
      return EFEITOS.filter((e) => e.value !== 'quantidade');
    },
    // Modelos compatíveis com a entidade e ainda NÃO presentes neste campo.
    modelosDisponiveis() {
      return modelosAplicaveis(this.modelos, this.gatilhos, this.montarResolvidas());
    },
  },
  data() {
    return {
      regrasLocais: [],
      operadores: OPERADORES,
    };
  },
  watch: {
    campo: {
      immediate: true,
      handler() {
        this.regrasLocais = (this.campo.regras || []).map((r) => {
          const cond = (r.condicoes && r.condicoes[0]) || {};
          return {
            efeito: r.efeito || (this.ehQuantidade ? 'quantidade' : this.somenteRotular ? 'rotular' : 'obrigatorio'),
            chave: cond.chave || (this.gatilhos[0] && this.gatilhos[0].campo) || '',
            operador: cond.operador || 'igual',
            valorTexto: this.formatarValor(cond.valor),
            rotulo: r.rotulo || '',
            min: r.min != null ? r.min : 1,
            max: r.max != null ? r.max : null,
          };
        });
      },
    },
  },
  methods: {
    formatarValor(valor) {
      if (Array.isArray(valor)) return valor.join(', ');
      return valor == null ? '' : String(valor);
    },
    parseValor(regra) {
      if (regra.operador === 'preenchido') return null;
      if (regra.operador === 'em') {
        return (regra.valorTexto || '')
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean);
      }
      return regra.valorTexto;
    },
    setMax(regra, val) {
      regra.max = val === '' || val == null ? null : Number(val);
      this.emitir();
    },
    labelGatilho(chave) {
      const g = this.gatilhos.find((x) => x.campo === chave);
      return g ? g.label : chave;
    },
    fonteDoGatilho(chave) {
      const g = this.gatilhos.find((x) => x.campo === chave);
      return (g && g.fonte) || 'campo';
    },
    // Gatilho booleano (tipo checkbox) → o VALOR da condição vira select Sim/Não.
    gatilhoBooleano(chave) {
      const g = this.gatilhos.find((x) => x.campo === chave);
      return !!g && g.tipo === 'checkbox';
    },
    labelOperador(value) {
      const o = this.operadores.find((x) => x.value === value);
      return o ? o.label : value;
    },
    labelEfeito(value) {
      const e = this.efeitos.find((x) => x.value === value);
      return e ? e.label : value;
    },
    resumo(regra) {
      const valor = regra.operador === 'preenchido' ? '' : ` ${regra.valorTexto || '…'}`;
      const cond = `quando ${this.labelGatilho(regra.chave)} ${this.labelOperador(regra.operador)}${valor}`;
      if (this.ehQuantidade) {
        const max = regra.max == null || regra.max === '' ? '∞' : regra.max;
        return `"${this.campo.label}" exige no mín. ${regra.min || 0} e no máx. ${max} ${cond}.`;
      }
      if (regra.efeito === 'bloqueia_cadastro') {
        return `O cadastro é BLOQUEADO ${cond}.`;
      }
      if (regra.efeito === 'rotular') {
        return `"${this.campo.label}" passa a se chamar "${regra.rotulo || '…'}" ${cond}.`;
      }
      return `"${this.campo.label}" fica ${this.labelEfeito(regra.efeito)} ${cond}.`;
    },
    adicionar() {
      this.regrasLocais.push({
        efeito: this.ehQuantidade ? 'quantidade' : this.somenteRotular ? 'rotular' : 'obrigatorio',
        chave: (this.gatilhos[0] && this.gatilhos[0].campo) || '',
        operador: 'igual',
        valorTexto: '',
        rotulo: '',
        min: 1,
        max: null,
      });
      this.emitir();
    },
    remover(idx) {
      this.regrasLocais.splice(idx, 1);
      this.emitir();
    },
    // Converte o estado interno (regrasLocais) no shape resolvido { efeito, conector, condicoes }.
    montarResolvidas() {
      return this.regrasLocais.map((r) => {
        const regra = {
          efeito: this.ehQuantidade ? 'quantidade' : r.efeito,
          conector: 'E',
          condicoes: [
            { fonte: this.fonteDoGatilho(r.chave), chave: r.chave, operador: r.operador, valor: this.parseValor(r) },
          ],
        };
        if (this.ehQuantidade) {
          regra.min = Number(r.min) || 0;
          regra.max = r.max == null || r.max === '' ? null : Number(r.max);
        } else if (r.efeito === 'rotular') {
          regra.rotulo = (r.rotulo || '').trim();
        }
        return regra;
      });
    },
    emitir() {
      this.$emit('input', this.montarResolvidas());
    },
    // ───── Modelos de regra ─────
    // Esta regra já está salva como modelo? (botão fica desabilitado + check)
    jaEhModelo(idx) {
      if (this.ehQuantidade) return false;
      const resolvida = this.montarResolvidas()[idx];
      return resolvida ? regraJaEhModelo(resolvida, this.modelos) : false;
    },
    salvarModelo(idx) {
      if (this.jaEhModelo(idx)) return; // já salva — não duplica
      const resolvida = this.montarResolvidas()[idx];
      if (resolvida) this.$emit('salvar-modelo', resolvida);
    },
    usarModelo(modelo) {
      const regra = modeloParaRegra(modelo);
      const cond = (regra.condicoes && regra.condicoes[0]) || {};
      this.regrasLocais.push({
        efeito: regra.efeito,
        chave: cond.chave || (this.gatilhos[0] && this.gatilhos[0].campo) || '',
        operador: cond.operador || 'igual',
        valorTexto: this.formatarValor(cond.valor),
        rotulo: regra.rotulo || '',
        min: 1,
        max: null,
      });
      this.emitir();
    },
  },
};
</script>

<style scoped>
.rcb { padding: 12px 4px; }
.rcb-empty { font-size: 12px; color: #98a2b3; padding: 6px 0 12px; }
.rcb-card {
  background: #fff; border: 1px solid #e4e7eb; border-radius: 10px;
  padding: 12px 14px; margin-bottom: 10px;
}
.rcb-linha { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
.rcb-prefix { font-size: 13px; color: #667085; }
/* Inputs no padrão do projeto (form-control), dimensionados inline */
.rcb-linha .form-control { height: 38px; }
.rcb-linha select.form-control { width: auto; min-width: 130px; }
.rcb-linha input.form-control { flex: 1; min-width: 200px; }
.rcb-efeito { color: #3538cd; font-weight: 600; }
/* input do novo rótulo (efeito `rotular`): compacto, não disputa flex com o valor */
.rcb-linha input.form-control.rcb-rotulo { flex: 0 0 auto; min-width: 140px; max-width: 200px; }
.rcb-qtd {
  display: inline-flex; align-items: center; gap: 6px; margin: 0;
  font-size: 13px; color: #3538cd; font-weight: 600;
}
.rcb-qtd .form-control { width: 64px; height: 38px; }
.rcb-modelo-btn { margin-left: auto; }
.rcb-remover { margin-left: 0; }
.rcb-acoes { display: flex; align-items: center; gap: 10px; }
.rcb-usar-modelo >>> .btn { font-size: 12px; }
.rcb-resumo {
  margin-top: 10px; font-size: 12px; color: #475467; font-style: italic;
  background: #f9fafb; border-radius: 6px; padding: 6px 10px;
}
.rcb-resumo-icon { color: #98a2b3; margin-right: 4px; }
</style>
